import { ambientHeaders } from '../../lib/ambientHeaders'
import { createAsyncThunk } from "@reduxjs/toolkit"
import apiRequest from "../../lib/http"
import { API_BASE } from '../../config/consts'
import axios from "axios"
import { ACCESS_TOKEN, StorageGetItem } from "../../lib/storage"
import { handleCompanyRefusal } from '../../lib/http'
import { normalizeError } from '../../utils/errorHelpers'
import { linkTokenHeaders, linkTokenRefusalOf } from '../../utils/linkToken'
import { LSA_NOT_READY_CODES } from '../../constants/lsaChargeName'

const chargeNameRefusalOf = (error) => {
  const first = error?.response?.data?.error?.errors?.[0]
  return LSA_NOT_READY_CODES.includes(first?.code) ? { code: first.code, message: first.message } : null
}

export const getDocuments = createAsyncThunk('documents/getDocuments', async ({
                                                                                limit, offset, userId
                                                                              }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/documents/${userId}`, { params: { limit, offset } })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to remove Bank method'
    return rejectWithValue(errorMessage)
  }
})
export const getUploadedDocuments = createAsyncThunk('documents/getUploadedDocuments', async ({
                                                                                                limit, offset, userId
                                                                                              }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/uploaded-documents/${userId}`, { params: { limit, offset } })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to remove Bank method'
    return rejectWithValue(errorMessage)
  }
})
export const getDocumentsTypes = createAsyncThunk('documents/getDocumentsTypes', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/documents-types`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to remove Bank method'
    return rejectWithValue(errorMessage)
  }
})


export const createDocument = createAsyncThunk(
  'documents/createDocument',
  async ({ body, type, fileType, userId, ip }, { rejectWithValue }) => {
    try {
      const authToken = await StorageGetItem(ACCESS_TOKEN)
      const headers = {
        'Content-Type': 'multipart/form-data',
        // These thunks bypass apiRequest, so nothing else attaches the office and brand headers for
        // them. Hand-built headers are exactly how a request ends up outside every guard.
        ...ambientHeaders(),
      }

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }
      const formData = new FormData()
      formData.append('file', body.file)

      const response = await axios.post(
        `${API_BASE}/files/${fileType}/${userId}?type=${type?._id}&name=${body.name}&ip=${ip}&subfolder=${encodeURIComponent(body.subfolder || '')}`,
        formData,
        { headers }
      )

      return response.data
    } catch (error) {
      // These thunks post with bare axios, so the shared response interceptor never sees this. A 403
      // naming a revoked or unselected brand would otherwise read as "Failed to upload document",
      // leave the stale brand in place, and let the next request fail the same way.
      await handleCompanyRefusal(error.response)

      const errorMessage =
        error.response?.data?.error?.errors[0]?.message || 'Failed to upload document'
      return rejectWithValue(errorMessage)
    }
  }
)

export const uploadLSAMainDoc = createAsyncThunk('documents/uploadLSAMainDoc',
  async ({ body, fileType, userId, ip }, { rejectWithValue }) => {
    try {
      const authToken = await StorageGetItem(ACCESS_TOKEN)
      const headers = {
        'Content-Type': 'multipart/form-data',
        // These thunks bypass apiRequest, so nothing else attaches the office and brand headers for
        // them. Hand-built headers are exactly how a request ends up outside every guard.
        ...ambientHeaders(),
      }

      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`
      }
      const formData = new FormData()
      formData.append('file', body.file)
      const response = await axios.post(
        `${API_BASE}/upload-main-doc/${fileType}/users/${userId}?ip=${ip}`,
        formData,
        { headers }
      )

      return response.data
    } catch (error) {
      // These thunks post with bare axios, so the shared response interceptor never sees this. A 403
      // naming a revoked or unselected brand would otherwise read as "Failed to upload document",
      // leave the stale brand in place, and let the next request fail the same way.
      await handleCompanyRefusal(error.response)

      const errorMessage =
        error.response?.data?.error?.errors[0]?.message || 'Failed to upload document'
      return rejectWithValue(errorMessage)
    }
  })

export const deleteDocuments = createAsyncThunk('documents/deleteDocuments', async (fileKey, { rejectWithValue }) => {
  try {
    const response = await apiRequest.delete(`/files/${fileKey}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to remove file'
    return rejectWithValue(errorMessage)
  }
})

export const updateDocuments = createAsyncThunk('documents/updateDocuments', async ({
                                                                                      documentId,
                                                                                      data,
                                                                                      type
                                                                                    }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/documents/${documentId}/?type=${type}`, data)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to update document'
    return rejectWithValue(errorMessage)
  }
})

export const getLsaTypeDoc = createAsyncThunk('documents/getLsaTypeDoc', async ({ userId, lsa }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/lsa-type/${userId}`, { params: lsa ? { lsa } : {} })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to resolve LSA type'
    return rejectWithValue(errorMessage)
  }
})

export const getDocument = createAsyncThunk('documents/getDocument', async (key, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/files/${key}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get document'
    return rejectWithValue(errorMessage)
  }
})

export const getDocumentInBlob = createAsyncThunk('documents/getDocumentInBlob', async (key, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/files/${key}/blob`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get document'
    return rejectWithValue(errorMessage)
  }
})

export const getTemplates = createAsyncThunk('documents/getTemplates', async (params, { rejectWithValue }) => {
  try {
    const { userId, typeDoc } = params
    const response = await apiRequest.get(`/templates/?userId=${userId}&typeDoc=${typeDoc}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get document template'
    return rejectWithValue(errorMessage)
  }
})

export const setTemplate = createAsyncThunk('documents/setTemplate', async (params, { rejectWithValue }) => {
  try {
    const { typeDoc, data, useEstimatedPayments } = params

    let url = `/template/?typeDoc=${typeDoc}`
    if (useEstimatedPayments !== undefined) {
      url += `&useEstimatedPayments=${useEstimatedPayments}`
    }

    const response = await apiRequest.post(url, data, { headers: linkTokenHeaders() })
    return response.data
  } catch (error) {
    const refusal = linkTokenRefusalOf(error)
    if (refusal) return rejectWithValue(refusal)
    const chargeNameRefusal = chargeNameRefusalOf(error)
    if (chargeNameRefusal) return rejectWithValue(chargeNameRefusal)
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message
      || error.response?.data?.message
      || 'Failed to generate document template'
    return rejectWithValue(errorMessage)
  }
})

export const signTemplate = createAsyncThunk('documents/signTemplate', async (params, { rejectWithValue }) => {
  try {
    const data = params
    const response = await apiRequest.post(`/sign-template`, data)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message
      || error.response?.data?.message
      || 'Failed to sign document'
    return rejectWithValue(errorMessage)
  }
})

const toSigningRefusal = (error, fallbackMessage) => {
  const data = error?.response?.data
  if (data == null) return { message: fallbackMessage, code: undefined }
  const first = data.error?.errors?.[0]
  if (first == null) return { message: undefined, code: undefined }
  return { message: first.message || fallbackMessage, code: first.code }
}

export const signMainDoc = createAsyncThunk('documents/signMainDoc', async (params, { rejectWithValue }) => {
  try {
    const data = params
    const response = await apiRequest.post(`/sign-main-doc`, data, { headers: linkTokenHeaders() })
    return response.data
  } catch (error) {
    return rejectWithValue(toSigningRefusal(error, 'Failed to sign document'))
  }
})

export const isSignedMainDoc = createAsyncThunk('documents/checkIsSignedLSA', async ({ userId }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/sign-main-doc/${userId}`)
    return response.data
  } catch (error) {
    return rejectWithValue(toSigningRefusal(error, 'Failed to sign document'))
  }
})

export const getSignDocRequest = createAsyncThunk('documents/getSignDocRequest', async (params, { rejectWithValue }) => {
  try {
    const { accessUUID } = params
    const response = await apiRequest.get(`/doc-sign/${accessUUID}`)
    return response.data
  } catch (error) {
    return rejectWithValue(toSigningRefusal(error, 'Failed to get sign document'))
  }
})

export const postSignDocRequest = createAsyncThunk('documents/postSignDocRequest', async (params, { rejectWithValue }) => {
  try {
    const { accessUUID, signedPdfBase64, typedName, consent } = params
    const body = typedName === undefined ? { signedPdfBase64 } : { signedPdfBase64, typedName, consent }
    const response = await apiRequest.post(`/doc-sign/${accessUUID}`, body)
    return response.data
  } catch (error) {
    return rejectWithValue(toSigningRefusal(error, 'Failed to sign document'))
  }
})

export const getSignDocuments = createAsyncThunk('documents/getSignDocuments', async (params, { rejectWithValue }) => {
  try {
    const { userId } = params
    const response = await apiRequest.get(`/signDocuments/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get document template'
    return rejectWithValue(errorMessage)
  }
})
export const resendSignDocument = createAsyncThunk('documents/resendSignDocument', async (params, { rejectWithValue }) => {
  try {
    const { docId } = params
    const { email } = params
    const response = await apiRequest.post(`/resend-sign/${docId}`, { email })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to resend e-sign document '
    return rejectWithValue(errorMessage)
  }
})

export const getUserSignDocuments = createAsyncThunk('documents/getUserSignDocuments', async (params, { rejectWithValue }) => {
  try {
    const { userId } = params
    const response = await apiRequest.get(`/signUserDocuments/${userId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error.errors[0].message || 'Failed to get docs'
    return rejectWithValue(errorMessage)
  }
})

export const downloadDocumentById = createAsyncThunk(
  'documents/download',
  async (params, { rejectWithValue }) => {
    try {
      const { docId } = params
      const response = await apiRequest.get(`/documents/${docId}/download`, {
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.errors?.[0]?.message || 'Failed to download document'
      return rejectWithValue(errorMessage)
    }
  }
)

export const getPoaAddressNotice = createAsyncThunk('documents/getPoaAddressNotice', async (userId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/poa-address-notice/${userId}`)
    return response.data.data
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})

export const generatePoaAddressNotice = createAsyncThunk('documents/generatePoaAddressNotice', async ({ userId, coApplicantId }, { rejectWithValue, fulfillWithValue }) => {
  try {
    const url = `/poa-address-notice/${userId}`
    const response = coApplicantId ? await apiRequest.post(url, { coApplicantId }) : await apiRequest.post(url)
    return fulfillWithValue(response.data.data, { alreadyGenerated: response.status === 200 })
  } catch (error) {
    return rejectWithValue(normalizeError(error))
  }
})
